import React, { useCallback, useMemo, useState } from 'react';
import type {
  ComputedScenarioTimings,
  ScenarioData,
} from '../../../lib/planningData';
import {
  calculatePnl,
  type PnlCost,
  type PnlIncome,
  type PnlItem,
} from '../pnl';
import Modal, { ModalBody } from '../../ui/Modal/Modal';
import Button from '../../ui/Button';
import PnlDebugView from './PnlDebugView';
import styled from 'styled-components';
import { formatCurrency } from '../../../lib/format';
import { Currency, useBunkerGradesQuery } from '../../../graphql';
import { type PnlProcessorContextInput } from '../context/context';
import { useCarbonPrice } from '../../hooks/useCarbonPrice';
import { type BunkerGrade } from '../co2/co2Processor';
import { type ClientId } from '@seaber/common/lib/clientId';
import { assertDefined } from '@seaber/common/lib/generics';
import type { CalculateTimeDataPnl, Pnl, PnlContextValue } from './context';
import { PnlContext } from './context';
import { useVoyageVesonBunker } from '../../integrations/VesonRob';
import type { PnlProcessorError } from '../processor';
import uniqBy from 'lodash/uniqBy';
import { useAsRef } from '../../../lib/util';
import { usePnlData } from '../../hooks/usePnlData';
import { useCurrencyRates, useDefaultCurrency } from '../../../state/zustand';
import type { Co2Emission } from '../co2/co2Cost';

interface PnlProviderProps extends React.PropsWithChildren {
  scenarioTimings: ComputedScenarioTimings;
}

export const isPnlDebuggerEnabled =
  typeof window !== 'undefined'
    ? !!window.localStorage.getItem('pnl-debug')
    : false;

export function PnlProvider(props: PnlProviderProps) {
  const orderById = props.scenarioTimings.useScenariosResult.scenarioOrderById;
  const orderByIdRef = useAsRef(orderById);
  const pnlData = usePnlData(
    orderByIdRef.current,
    props.scenarioTimings.data,
    props.scenarioTimings.portCostEstimateMaps,
  );
  const selectedScenario = props.scenarioTimings.selectedScenarioData;
  const scheduleData = props.scenarioTimings.scheduleContext;

  const currencyRates = useCurrencyRates();
  const defaultCurrency = useDefaultCurrency();

  const carbonPriceData = useCarbonPrice();
  const { loading: _bunkerGradesLoading, data: bunkerGradesData } =
    useBunkerGradesQuery();
  const {
    vesonBunkerData: imosBunkerData,
    isEnabled: _isImosRobEnabled,
    isLoading: _isImosRobLoading,
  } = useVoyageVesonBunker();

  // TODO: Move gathering of inputs to separate hooks to keep this component smaller
  const bunkerGradeById = useMemo(() => {
    const map = new Map<number, BunkerGrade>();

    for (const bunkerGrade of bunkerGradesData?.allBunkerGradeOverrides
      ?.nodes ?? []) {
      if (!bunkerGrade) continue;

      map.set(bunkerGrade.id, {
        id: bunkerGrade.id,
        carbonFactor: bunkerGrade.carbonFactor
          ? Number(bunkerGrade.carbonFactor)
          : null,
        ch4Factor: bunkerGrade.ch4Factor ? Number(bunkerGrade.ch4Factor) : null,
        lcv: bunkerGrade.lcv ? Number(bunkerGrade.lcv) : null,
        n2oFactor: bunkerGrade.n2OFactor ? Number(bunkerGrade.n2OFactor) : null,
        wttFactor: bunkerGrade.wttFactor ? Number(bunkerGrade.wttFactor) : null,
      });
    }

    return map;
  }, [bunkerGradesData]);

  const calculateTimeDataPnl = useCallback<CalculateTimeDataPnl>(
    (tds, context) => {
      if (!currencyRates) {
        return {
          costs: [],
          errors: [],
          extras: {
            co2Processor: {
              co2Emissions: [],
            },
          },
          income: [],
        };
      }

      try {
        const pnl = calculatePnl(tds, {
          bunkerCost: {
            imosBunkerData,
          },
          co2Cost: {
            bunkerGradeById,
            carbonPrice: {
              carbonPricePerTonne: carbonPriceData.carbonPrice,
              isOverride: carbonPriceData.isOverride,
              updatedAt: carbonPriceData.updatedAt
                ? String(carbonPriceData.updatedAt)
                : null,
            },
          },
          context: {
            currency: {
              defaultCurrency,
              rates: currencyRates,
            },
            locationById: scheduleData.locations,
            type: context.type,
            vessel: context.vessel,
          },
          freight: {
            orderById: orderByIdRef.current,
            overrides: {
              incomingFreightOverrideByOrder: null,
            },
            pnlData,
          },
        });

        return pnl;
      } catch (err) {
        console.error(`Error calculating pnl`, err);
        return {
          costs: [],
          errors: [],
          extras: {
            co2Processor: {
              co2Emissions: [],
            },
          },
          income: [],
        };
      }
    },
    [
      bunkerGradeById,
      carbonPriceData.carbonPrice,
      carbonPriceData.isOverride,
      carbonPriceData.updatedAt,
      currencyRates,
      defaultCurrency,
      imosBunkerData,
      orderByIdRef,
      pnlData,
      scheduleData.locations,
    ],
  );

  const calculateTimelinePnl = useCallback(
    (timeline: (typeof selectedScenario.timelines)[number]) => {
      try {
        const errors: PnlProcessorError[] = [];
        const costs: PnlCost[] = [];
        const income: PnlIncome[] = [];
        const pnlByShipmentId = new Map<ClientId, Pnl>();
        const co2Emissions: {
          all: Co2Emission[];
          byShipmentId: Map<ClientId, Co2Emission[]>;
        } = {
          all: [],
          byShipmentId: new Map(),
        };

        const vessel: PnlProcessorContextInput['vessel'] = timeline.vessel
          ? {
              grossTonnage: timeline.vessel.grossTonnage
                ? Number(timeline.vessel.grossTonnage)
                : null,
              name: timeline.vessel.name ?? '',
              id: timeline.vessel.id,
              shortName: timeline.vessel.shortName ?? null,
            }
          : null;

        for (const vg of timeline.voyageGroups) {
          const pnl = calculateTimeDataPnl(vg.events, {
            type: timeline.type === 'tc' ? 'vessel' : 'contract',
            vessel,
          });

          for (const cost of pnl.costs) {
            if (!cost.shipmentId) continue;

            const shipmentPnl =
              pnlByShipmentId.get(cost.shipmentId) ||
              pnlByShipmentId
                .set(cost.shipmentId, {
                  costs: [],
                  errors: [],
                  income: [],
                })
                .get(cost.shipmentId);

            assertDefined(shipmentPnl, 'pnl for shipmentId should be defined');

            shipmentPnl.costs.push(cost);
          }

          for (const income of pnl.income) {
            if (!income.shipmentId) continue;

            const shipmentPnl =
              pnlByShipmentId.get(income.shipmentId) ||
              pnlByShipmentId
                .set(income.shipmentId, {
                  costs: [],
                  errors: [],
                  income: [],
                })
                .get(income.shipmentId);

            assertDefined(shipmentPnl, 'pnl for shipmentId should be defined');
            shipmentPnl.income.push(income);
          }

          for (const emission of pnl.extras.co2Processor.co2Emissions) {
            if (emission.shipmentId) {
              const shipmentEmissions =
                co2Emissions.byShipmentId.get(emission.shipmentId) ||
                co2Emissions.byShipmentId
                  .set(emission.shipmentId, [])
                  .get(emission.shipmentId);

              assertDefined(
                shipmentEmissions,
                'shipment emissions should be defined',
              );

              shipmentEmissions.push(emission);
            }

            co2Emissions.all.push(emission);
          }

          // Add timeline information to each cost item
          const costsWithTimeline = pnl.costs.map((cost) => ({
            ...cost,
            timelineId: timeline.id,
            timelineName: timeline.title,
            timelineType: timeline.type,
            vesselName: vessel?.name || 'Unknown',
          }));

          const incomesWithTimeline = pnl.income.map((income) => ({
            ...income,
            timelineId: timeline.id,
            timelineName: timeline.title,
            timelineType: timeline.type,
            vesselName: vessel?.name || 'Unknown',
          }));

          costs.push(...costsWithTimeline);
          income.push(...incomesWithTimeline);
          errors.push(...pnl.errors);
        }

        return {
          errors,
          costs,
          income,
          pnlByShipmentId,
          co2Emissions,
        };
      } catch (err) {
        console.error(
          `Error in calculateTimelinePnl for timeline ${timeline?.id ?? 'unknown'}`,
          err,
        );
        return {
          errors: [],
          costs: [],
          income: [],
          pnlByShipmentId: new Map<ClientId, Pnl>(),
          co2Emissions: {
            all: [],
            byShipmentId: new Map<ClientId, Co2Emission[]>(),
          },
        };
      }
    },
    [selectedScenario, calculateTimeDataPnl],
  );

  const calculateScenarioPnl = useCallback(
    (scenario: ScenarioData) => {
      try {
        const errors: PnlProcessorError[] = [];
        const costs: PnlCost[] = [];
        const income: PnlIncome[] = [];
        const pnlByShipmentId = new Map<ClientId, Pnl>();
        const co2Emissions: {
          all: Co2Emission[];
          byShipmentId: Map<ClientId, Co2Emission[]>;
        } = {
          all: [],
          byShipmentId: new Map(),
        };

        for (const timeline of scenario.timelines) {
          const result = calculateTimelinePnl(timeline);

          errors.push(...result.errors);
          costs.push(...result.costs);
          income.push(...result.income);
          co2Emissions.all.push(...result.co2Emissions.all);

          // Merge pnlByShipmentId
          for (const [
            shipmentId,
            pnlResult,
          ] of result.pnlByShipmentId.entries()) {
            if (!pnlByShipmentId.has(shipmentId)) {
              pnlByShipmentId.set(shipmentId, pnlResult);
            } else {
              pnlByShipmentId.get(shipmentId)!.costs.push(...pnlResult.costs);
            }
          }

          // Merge co2 emissions by shipmentId
          for (const [
            shipmentId,
            emissions,
          ] of result.co2Emissions.byShipmentId.entries()) {
            if (!co2Emissions.byShipmentId.has(shipmentId)) {
              co2Emissions.byShipmentId.set(shipmentId, emissions);
            } else {
              co2Emissions.byShipmentId.get(shipmentId)!.push(...emissions);
            }
          }
        }

        return {
          errors,
          co2Emissions,
          costs,
          income,
          pnlByShipmentId,
        };
      } catch (err) {
        console.error(
          `Error in calculateScenarioPnl for scenario ${
            scenario.scenario.name ?? 'unknown'
          }`,
          err,
        );
        return {
          errors: [],
          co2Emissions: {
            all: [],
            byShipmentId: new Map<ClientId, Co2Emission[]>(),
          },
          costs: [],
          income: [],
          pnlByShipmentId: new Map<ClientId, Pnl>(),
        };
      }
    },
    [calculateTimelinePnl],
  );

  const { errors, costs, income, pnlByShipmentId, co2Emissions } = useMemo(
    () => calculateScenarioPnl(selectedScenario),
    [calculateScenarioPnl, selectedScenario],
  );

  const contextValue: PnlContextValue = useMemo(() => {
    // Remove duplicate errors for now
    const uniqueErrors = uniqBy(
      errors,
      (err) => `${err.processor}-${err.error}`,
    );

    return {
      calculateTimeDataPnl,
      emissions: {
        co2: co2Emissions,
      },
      pnl: {
        byShipmentId: pnlByShipmentId,
        all: {
          costs,
          errors: uniqueErrors,
          income,
        },
      },
    };
  }, [
    errors,
    co2Emissions,
    costs,
    income,
    calculateTimeDataPnl,
    pnlByShipmentId,
  ]);

  const allPnlItems = useMemo(() => {
    return [...costs, ...income];
  }, [costs, income]);

  return (
    <PnlContext.Provider value={contextValue}>
      {props.children}
      {isPnlDebuggerEnabled ? <PnlDebug pnlItems={allPnlItems} /> : null}
    </PnlContext.Provider>
  );
}

interface PnlDebugProps {
  pnlItems: PnlItem[];
}

function PnlDebug(props: PnlDebugProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <DebugButton onPress={() => setIsOpen(true)}>Debug PnL</DebugButton>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        width="95%"
        maxWidth="1600px"
        height="90%"
      >
        <ModalBody>
          {props.pnlItems.length > 0 && (
            <>
              <div
                style={{
                  marginBottom: 16,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold', marginRight: 8 }}>
                    Total Cost:
                  </span>
                  <span>
                    {formatCurrency(
                      props.pnlItems
                        .filter((item) => item.type === 'cost')
                        .reduce((sum, cost) => sum + cost.amount, 0),
                      2,
                      Currency.EUR,
                    )}
                  </span>
                </div>
              </div>

              <PnlDebugView pnlItems={props.pnlItems} />
            </>
          )}

          {props.pnlItems.length === 0 && (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
              }}
            >
              <h3>No PnL data available</h3>
              <p>Make sure scenario data is loaded correctly.</p>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

const DebugButton = styled(Button)`
  position: fixed;
  top: 8px;
  left: 50%;
  z-index: 500;
`;
